import {
  AUTH_PREFIX,
  Comment,
  FileEntity,
  Space,
  SpaceTrackRow,
  SUBSCRIPTION_INTERVAL,
  SUBSCRIPTION_TIER,
  SUBSCRIPTION_TIER_FORMATTED,
  Track,
  UserEntity,
  UserMetadata,
} from "@highnote/server/src/core/entities";
import {
  getActiveSpacesLimitByTier,
  getFullRoles,
  getInviteEmail,
  getStorageLimitByTier,
  getTrackVersionsLimitByTier,
  isAllowed,
  isInviteId,
  isSpacePrivateInboxEnabled,
  MembershipResolver,
  PERMISSION,
  TYPEFORM_WELCOME_SURVEY_ID,
} from "@highnote/server/src/core/shared-util";
import { highnote } from "@highnote/server/src/sdk";
import { Widget } from "@typeform/embed-react";
import LoadingScreen from "App/common/LoadingScreen/LoadingScreen";
import { ToastMessageContent, useToast } from "App/common/useToast";
import {
  FROM_MOBILE_APP_FLAG,
  MOBILE_TOKEN_FLAG,
  routePaths,
  sanitizeCurrentURL,
  withMobileAuthDeeplinkRoot,
} from "App/modules/urls";
import { formatFileSize, setTestProperty } from "App/modules/utils";
import {
  indexedDBLocalPersistence,
  setPersistence,
  signInAnonymously,
  signInWithCustomToken,
} from "firebase/auth";
import { debounce } from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router";
import { v4 as uuidv4 } from "uuid";
import {
  MembershipClientFlags,
  MembershipFlagsContextProvider,
  useMembershipFlags,
} from "../MembershipFlagsContext";
import {
  PublicRolesContextProvider,
  usePublicRoles,
} from "../PublicRolesContext";
import { reportMembershipMiss } from "App/modules/membershipTelemetry";
import { UserContextProvider, useUser } from "../useEntities/useUser";
import { JoinEntityDialog } from "./JoinEntityDialog";
import {
  MyMembershipsContextProvider,
  useMyMemberships,
} from "./MyMembershipsContext";
import { JOIN_ENTITY_TRIGGER, JoinEntityConfig } from "./util";
import { useMobileAppParams } from "App/routes/Main/useMobileAppParams";
import { AUTH_TYPE } from "./AuthForm";

export const getAuthUrl = (params?: Record<string, string>) => {
  let url = "/login?";

  if (params) {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) sp.set(key, value);
    });
    url += `${sp.toString()}&`;
  }

  const existingParams = new URLSearchParams(location.search);
  const returnTo =
    existingParams.get("returnTo") ||
    encodeURIComponent(`${location.pathname}${location.search}`);
  return `${url}returnTo=${returnTo}`;
};

export const getIsInvitedUserLogin = () => {
  const query = new URLSearchParams(location.search);
  const queryHasReferrerType = query.has("referrer_type");
  const queryHasReferredId = query.has("referrer_id");
  const queryHasEmail = query.has("email");
  const queryHasReturnTo = query.has("returnTo");
  if (
    queryHasEmail &&
    queryHasReferredId &&
    queryHasReferrerType &&
    queryHasReturnTo
  ) {
    return true;
  }

  return false;
};

export const linkAccount = async ({
  method,
  identifier,
  password,
  token,
}: {
  method: "email" | "sms" | "token";
  identifier?: string;
  password?: string;
  token?: string;
}) => {
  return highnote.authenticateUser({
    method,
    identifier,
    password,
    token,
    link: true,
  });
};

// Array of param keys that are mapped directly
const AUTH_PARAM_KEYS = ["subscription", "coupon_code", "returnTo", "trigger"];

export const loginWithProvider = async ({
  connection,
  type = AUTH_TYPE.LOG_IN,
  returnTo,
}: {
  connection: AUTH_PREFIX.APPLE | AUTH_PREFIX.GOOGLE;
  type: AUTH_TYPE;
  returnTo?: string;
}) => {
  const domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;

  if (!domain || !clientId) {
    throw new Error("Missing Auth0 configuration");
  }

  // Get existing query params from the current URL
  const currentParams = new URLSearchParams(window.location.search);

  const redirectParams = AUTH_PARAM_KEYS.reduce((acc, key) => {
    const currentParamVal = currentParams.get(key);
    if (currentParamVal) {
      acc.set(key, currentParamVal);
    }
    return acc;
  }, new URLSearchParams());

  if (currentParams.get("frequency")) {
    redirectParams.set(
      "frequency",
      currentParams.get("frequency").toLowerCase(),
    );
  }

  if (currentParams.get("hideWelcomeSurvey")) {
    redirectParams.set("hideWelcomeSurvey", "true");
  }
  if (returnTo) {
    redirectParams.set("returnTo", returnTo);
  }

  const auth0Params = new URLSearchParams({
    audience: `https://dev-bdp0xr9n.auth0.com/api/v2/`,
    response_type: "token",
    connection,
    client_id: clientId,
    redirect_uri:
      encodeURI(
        `${location.origin}${type === AUTH_TYPE.LINK ? "/link" : "/login"}`,
      ) + `${redirectParams.toString() ? `?${redirectParams.toString()}` : ""}`, // redirectParams already encoded via URLSearchParams
    blockDuplicates: String(type !== AUTH_TYPE.LINK),
  });

  // Redirect to Auth0 login page
  location.href = `https://${domain}/authorize?${auth0Params.toString()}`;
};

export const logIn = async ({
  method,
  name,
  identifier,
  password,
  token,
  metadata,
  trigger,
  subscription,
  coupon_code,
  frequency,
  hasAuthError,
  firstName,
  lastName,
  smsUserEmail,
}: {
  method: "email" | "sms" | "token";
  name?: string;
  identifier?: string;
  password?: string;
  token?: string;
  metadata?: object;
  trigger?: JOIN_ENTITY_TRIGGER;
  subscription?: SUBSCRIPTION_TIER;
  coupon_code?: string;
  frequency?: SUBSCRIPTION_INTERVAL;
  hasAuthError?: boolean;
  firstName?: string;
  lastName?: string;
  smsUserEmail?: string;
}): Promise<{
  uid: string;
  loginCount: number;
  redirectPath?: string;
}> => {
  // get access token & refresh token
  const { firebaseToken, loginCount, redirectPath } =
    await highnote.authenticateUser({
      method,
      name,
      identifier,
      password,
      token,
      metadata,
      trigger,
      subscription: subscription,
      coupon_code: coupon_code || "",
      frequency:
        (frequency?.toLowerCase() as SUBSCRIPTION_INTERVAL) ||
        SUBSCRIPTION_INTERVAL.MONTHLY,
      firstName,
      lastName,
      smsUserEmail,
    });

  if (!firebaseToken) {
    console.log("AUTH FAILED");
    return;
  }

  await signInWithCustomToken(highnote.__firebaseAuth, firebaseToken as string);

  const fromMobileApp = sessionStorage.getItem(FROM_MOBILE_APP_FLAG) === "true";
  if (fromMobileApp) {
    // Get user data to check welcome survey status
    const user = await highnote.getUserPublic({
      id: highnote.__firebaseAuth.currentUser.uid,
    });

    // Only redirect if welcome survey is completed
    if (user?.completedWelcomeSurvey) {
      handleMobileAppRedirect(firebaseToken, hasAuthError);
    }
    // If survey not completed, keep FROM_MOBILE_APP_FLAG in sessionStorage
    // so WelcomeSurvey component can handle redirect after completion
  }

  console.log("AUTH SUCCEEDED", highnote.__firebaseAuth.currentUser);
  return {
    uid: highnote.__firebaseAuth.currentUser?.uid,
    loginCount,
    redirectPath,
  };
};

const handleMobileAppRedirect = (
  firebaseToken: string,
  hasAuthError?: boolean,
) => {
  try {
    const pathname = location.pathname;
    const isLogout = pathname.startsWith("/logout");

    if (isLogout || hasAuthError) {
      return;
    }

    const deepLinkUrl = `${withMobileAuthDeeplinkRoot(`login?token=${encodeURIComponent(firebaseToken)}`)}`;
    window.location.href = deepLinkUrl;
    sessionStorage.removeItem(FROM_MOBILE_APP_FLAG);
  } catch (error) {
    console.log("Error in handleMobileAppRedirect: " + error);
  }
};

type AuthContextProps = {
  user: UserEntity;
  userMetadata: UserMetadata;
  authLoading: boolean;
  refreshAuth: () => void;
  refreshShareKey: () => Promise<void>;
  storageLimit: number;
  activeSpacesLimit: number;
  trackVersionsLimit: number;
  hideWelcomeSurvey: boolean;
  setHideWelcomeSurvey: (hideWelcomeSurvey: boolean) => void;
  shareKey?: string;
  isPublicView: (entity: Space) => boolean;
  isAllowed: (
    key: PERMISSION,
    entities: {
      space?: Space;
      comment?: Comment;
      track?: Track;
      file?: FileEntity;
      spaceTrackRow?: SpaceTrackRow;
    },
  ) => boolean;
  joinEntity: (props: JoinEntityConfig, force?: boolean) => void;
};

const AuthContext = React.createContext<AuthContextProps>({
  user: undefined,
  userMetadata: undefined,
  authLoading: true,
  refreshAuth: () => {},
  refreshShareKey: () => Promise.resolve(),
  shareKey: undefined,
  storageLimit: Infinity,
  activeSpacesLimit: Infinity,
  trackVersionsLimit: Infinity,
  hideWelcomeSurvey: false,
  setHideWelcomeSurvey: () => {},
  joinEntity: () => {},
  isPublicView: () => false,
  isAllowed: () => false,
});

const WelcomeSurvey = React.memo(
  ({
    userId,
    welcomeBack,
    hasEmail,
    hasFirstName,
    hasLastName,
    userSubscriptionTier,
  }: {
    userId: string;
    welcomeBack: boolean;
    hasEmail: boolean;
    hasFirstName: boolean;
    hasLastName: boolean;
    userSubscriptionTier?: SUBSCRIPTION_TIER;
  }) => {
    return (
      <Widget
        id={TYPEFORM_WELCOME_SURVEY_ID}
        key={userId}
        hidden={{
          user_id: userId,
          welcome_back: String(!!welcomeBack),
          has_email: String(hasEmail),
          has_first_name: String(hasFirstName),
          has_last_name: String(hasLastName),
          ...(userSubscriptionTier && {
            subscription: String(
              SUBSCRIPTION_TIER_FORMATTED[userSubscriptionTier],
            ),
          }),
          ...(window.location.pathname.startsWith("/dropbox") && {
            referrer: "dropbox",
          }),
          ...(window.location.pathname.startsWith("/submit") && {
            referrer: "track_submission",
          }),
        }}
        className={`typeform-welcome-survey`}
        onSubmit={async () => {
          await highnote.updateUser({
            id: userId,
            data: {
              completedWelcomeSurvey: true,
            },
          });

          const fromMobileApp =
            sessionStorage.getItem(FROM_MOBILE_APP_FLAG) === "true";

          if (fromMobileApp) {
            const publicMe = await highnote.getUserPublic({
              id: userId,
              generateFirebaseToken: fromMobileApp,
            });
            const token = publicMe?.firebaseToken;

            if (token) {
              handleMobileAppRedirect(token);
            }
          }
        }}
        inlineOnMobile={true}
      />
    );
  },
);
WelcomeSurvey.displayName = "WelcomeSurvey";

const _AuthContextProvider = ({
  loading,
  userMetadata,
  shareKey,
  reactiveAuthId,
  hideWelcomeSurvey,
  refreshAuth,
  refreshShareKey,
  children,
  setHideWelcomeSurvey,
}: {
  loading: boolean;
  shareKey?: string;
  /**
   * The reactive mirror of `getAuthId()`, threaded down from
   * `AuthContextProvider`'s `onAuthStateChanged` handler. This provider's
   * body reads `getAuthId()`, and that read is not in the value memo's
   * dependency array, so the memo's closure can capture a stale identity.
   * That is harmless while the answer comes out of the entity the caller
   * passed in, and not harmless once the answer comes from a per-identity
   * member-document stream. Only the flagged branch consumes this; the
   * legacy engine keeps its direct `getAuthId()` read.
   */
  reactiveAuthId?: string;
  userMetadata?: UserMetadata;
  hideWelcomeSurvey: boolean;
  refreshAuth: (options?: { hideWelcomeSurvey?: boolean }) => void;
  refreshShareKey: () => Promise<void>;
  children: React.ReactNode;
  setHideWelcomeSurvey: (hideWelcomeSurvey: boolean) => void;
}) => {
  const history = useHistory();
  const authId = getAuthId();
  const { addMessage } = useToast();
  const { user, userLoading } = useUser();
  const { isAuthorizedMobileComponent } = useMobileAppParams();
  const [showJoinEntity, setShowJoinEntity] = useState<boolean>(false);
  const joinEntityConfigRef = useRef<JoinEntityConfig>();
  const dismissedJoinEntityRef = useRef<boolean>(false);
  const [isCheckingInvite, setCheckingInvite] = useState<boolean>(true);
  const firstLoginRef = useRef<boolean>(false);

  const storageLimit = getStorageLimitByTier(user?.subscriptionTier);
  const activeSpacesLimit = getActiveSpacesLimitByTier(user?.subscriptionTier);
  const trackVersionsLimit = getTrackVersionsLimitByTier(
    user?.subscriptionTier,
  );

  /* The permission resolver.
   *
   * The entire cutover for all 69 `isAllowed` consumer sites and all 6
   * `<PermissionTooltip permission={…}>` sites is this memo plus the six
   * lines in the value memo below. Not one of those files changes. The
   * public signature of `AuthContext.isAllowed` is untouched, so the two
   * Jest files that mock it pass unchanged.
   *
   * Warning: three of the 69 do not go through this context, so they never
   * see the resolver: the direct `isAllowed(..., shareKey, ...)` call in
   * `routes/Main/Space/SpaceNavigation.tsx` and the two calls in
   * `routes/Main/Space/TrackVersionTabs.tsx`. They import `isAllowed`
   * straight from `core/shared-util` and call the three-argument form with
   * an explicit share-key identity, which a comment at that call site
   * instructs. They still compile (the 4th parameter is optional) and still
   * answer from `getFullRoles`. While the migration still writes the legacy
   * fields, that answer is correct, and the migration guarantees that write
   * for now. Once the migration deletes the legacy fields, it becomes a hard
   * deny. So all three are on the migration's list of call sites to fix
   * before that deletion. */
  const { resolveMember, loading: membershipsLoading } = useMyMemberships();
  const { getPublicRoles } = usePublicRoles();
  const { membersV2Enabled } = useMembershipFlags();

  const membershipResolver = useMemo<MembershipResolver | undefined>(() => {
    /* `undefined` with the flag off, so `hasRole` skips the resolver
     * entirely and takes `getFullRoles(entity)` exactly as today. This is
     * the rollback. */
    if (!membersV2Enabled) return undefined;

    return {
      getEffectiveRoles: ({ identifier, spaceId }) => {
        if (!spaceId || !identifier) return undefined;
        if (membershipsLoading) return undefined;

        const member = resolveMember(spaceId, identifier);
        if (member === undefined) {
          /* The counter that gates the deletion of the legacy fields. A miss
           * is harmless now, because we fall back to the legacy map, and
           * fatal once the migration deletes those fields, because the
           * fallback then becomes a deny. */
          reportMembershipMiss(spaceId);
          return undefined;
        }

        /* `space.publicRoles` is the stored mirror, derived from
         * readability, of the `roles[PUBLIC_ID]` union that `hasRole`
         * applies to every identity today. Without it, every check on a
         * public space grants too little, and controls silently disappear
         * for visitors. */
        const publicRoles = getPublicRoles(spaceId);
        if (member === null) return [...publicRoles];
        return [...(member.effectiveRoles || []), ...publicRoles];
      },
      getIsTopLevel: ({ identifier, spaceId }) => {
        if (!spaceId || !identifier || membershipsLoading) return undefined;
        const member = resolveMember(spaceId, identifier);
        if (member === undefined) return undefined;
        return member?.isTopLevel === true;
      },
    };
  }, [membersV2Enabled, membershipsLoading, resolveMember, getPublicRoles]);

  useEffect(() => {
    // For Cypress (see cypress/support/commands.js)
    setTestProperty("logIn", logIn);
  }, []);

  useEffect(() => {
    if (!user) return;

    try {
      const remaining = formatFileSize(storageLimit - user.storageUsed);
      console.log(`[DEBUG] User Subscription Tier: ${user.subscriptionTier}`);
      console.log(
        `[DEBUG] User Storage: ${user.storageUsed} / ${storageLimit}; ${remaining} remaining`,
      );
      console.log(
        `[DEBUG] User Spaces: ${user.spacesUsed} / ${activeSpacesLimit}`,
      );
      console.log(`[DEBUG] User Track: ${user.tracksUsed} / ${Infinity}`);
      console.log(
        `[DEBUG] User Track Versions: ${user.trackVersionsUsed} / ${trackVersionsLimit}`,
      );
    } catch (e) {
      console.log("[DEBUG] Could not log user storage stats.");
    }

    if (user.stripeCustomerId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tap = (window as any).tap;

      if (tap) {
        try {
          tap("trial", user.stripeCustomerId);
        } catch (e) {
          // empty
        }

        if (user.firstStripeChargeId) {
          try {
            tap(
              "conversion",
              user.firstStripeChargeId,
              // stripe gives us cents, tapfiliate wants dollars
              user.firstStripeChargeAmount / 100,
              { customer_id: user.stripeCustomerId },
            );
          } catch (e) {
            // empty
          }
        }
      }
    }

    if (user.deletedAuth0) {
      history.push(routePaths.logout);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.firstLogIn) return;
    firstLoginRef.current = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataLayer = (window as any).dataLayer;
    if (dataLayer) {
      dataLayer.push({ event: "Signed_Up" });
      highnote.updateUser({ id: user.id, data: { firstLogIn: false } });
    }
  }, [user?.firstLogIn]);

  const showWelcomeSurvey = useMemo(() => {
    const isFromMobileApp =
      sessionStorage.getItem(FROM_MOBILE_APP_FLAG) === "true";
    const exceptions = ["/signup", "/login", "/link", "/logout"];

    if (
      exceptions.some((p) => location.pathname.startsWith(p)) &&
      !isFromMobileApp
    )
      return false;

    // Intermediary page for mobile app to handoff to a space, share key, and track id
    if (location.pathname.startsWith("/space-handoff")) return false;

    // Track submission flow — profile fields captured directly in sign-up form.
    // Also suppress for users who came through form submission (even on /library etc.)
    if (location.pathname.startsWith("/submit")) return false;
    if (user?.user_metadata?.referrer_type === "form_submission") return false;

    // If the current page is an authorized mobile component, don't show the welcome survey
    if (isAuthorizedMobileComponent) return false;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).Cypress) return false;
    if (!user) return false;
    if (hideWelcomeSurvey) return false;

    return !user.completedWelcomeSurvey;
  }, [
    hideWelcomeSurvey,
    userLoading,
    user,
    shareKey,
    isAuthorizedMobileComponent,
  ]);

  useEffect(() => {
    if (userLoading) return;
    const query = new URLSearchParams(location.search);
    const pushToAuth = query.has("auth");

    const recipientId = (query.get("recipient") || "").replace(" ", "+");
    const isSpace = location.pathname.includes("/space/");
    const isCollection = location.pathname.includes("/collection/");

    if (!isSpace && !isCollection) {
      setCheckingInvite(false);
      return;
    }

    let redirectUrl = getAuthUrl();

    // If you land on a space or a collection page with a `recipient` specified,
    // this is an entity member invite link. Get the relevant redirect URL.
    if (recipientId) {
      const senderName = (query.get("senderName") || "").replace(" ", "+");
      const senderId = (query.get("senderId") || "").replace(" ", "+");
      const entityType = "Space";
      const referrer_type = "space_member_email";

      const banner = `${
        senderName || "Someone"
      } invited you to join their ${entityType} on Highnote.`;

      let recipientEmail: string;

      if (isInviteId(recipientId)) {
        recipientEmail = getInviteEmail(recipientId);
        if (recipientEmail === user?.email) {
          setCheckingInvite(false);
          return;
        }
      }

      redirectUrl = getAuthUrl({
        banner,
        referrer_type,
        referrer_id: senderId,
        email: recipientEmail,
      });
      // If the invitation is for a different user, have the user sign-in again
      if (user && user.id !== recipientId) {
        const toastId = uuidv4();
        addMessage({
          id: toastId,
          children: (
            <ToastMessageContent
              type="error"
              id={toastId}
              text="Invalid invitation. Please sign in again."
            />
          ),
          expireAt: Date.now() + 10000,
        });
        setCheckingInvite(false);
        history.push(redirectUrl);
        return;
      }
    }

    // If there is an '?auth' param and you're not logged in
    // you will be redirected to the login page regardless of whether
    // or not it's an invite link.
    if (pushToAuth) {
      if (!user) {
        setCheckingInvite(false);
        history.push(redirectUrl);
        return;
      }
      query.delete("auth");
      history.replace(`${location.pathname}?${query.toString()}`);
    }

    if (!recipientId) {
      setCheckingInvite(false);
      return;
    }

    // If it's not a new user invite OR the invited user is already
    // logged in, we don't need to do anything further.
    if (recipientId === user?.id) {
      setCheckingInvite(false);
      return;
    }

    history.push(redirectUrl);
    setCheckingInvite(false);
  }, [userLoading, user?.id, user?.email]);

  const isPublicView = useCallback(
    (entity: Space) => {
      if (!entity) return false;

      if (membersV2Enabled) {
        /* Presence, not readability. This gate is a presence test today: a
         * user blocked with `rolesV2[me] === []` is not a public view, even
         * though they cannot read anything. Nine call sites in six files,
         * plus `joinEntity`'s own guard below, depend on that. A member
         * document exists for every key present in `getFullRoles(space)`
         * today, keys whose role array is `[]` included; the backfill
         * contract of this migration buys exactly that. If the backfill ever
         * skips `[]` grants, ten UI gates flip for blocked users and nothing
         * errors.
         *
         * Warning: `user?.id`, not the reactive `authId`. They differ for
         * anonymous sessions: the auth handler deliberately leaves
         * `currentUserId` undefined for anonymous users, while it sets
         * `authId` for them. Today an anonymous identity that holds a grant
         * still reads as a public view, because
         * `Object.keys(roles).includes(undefined)` is false. `authId` here
         * quietly changes that. Preserve today exactly.
         *
         * Warning: `user?.id ? … : null` rather than `: undefined` is
         * deliberate. A signed-out session has no uid, and "there is no uid"
         * is a confirmed absence, not an unknown one. An `undefined` there
         * pins every signed-out visitor to the legacy path forever, and the
         * flag becomes untestable for exactly the population that public
         * spaces exist for. */
        const mine = user?.id ? resolveMember(entity.id, user.id) : null;
        const viaShare = shareKey ? resolveMember(entity.id, shareKey) : null;

        /* Only answer from member documents when both identities are known.
         * `undefined` means "I cannot answer", never "no access". */
        if (mine !== undefined && viaShare !== undefined) {
          return mine === null && viaShare === null;
        }
      }

      const roles = getFullRoles(entity);

      // If you don't have access through your user ID or your share key,
      // it must be publicly visible.
      return (
        !Object.keys(roles).includes(user?.id) &&
        !Object.keys(roles).includes(shareKey)
      );
    },
    [user, shareKey, membersV2Enabled, resolveMember],
  );

  const joinEntity = useCallback(
    (config: JoinEntityConfig, force?: boolean) => {
      // TODO: Many places we are passing an undefined entity to joinEntity.
      // This is due to the fact we are trying to pass a space via useSpaceContext()
      // which is not available outside of SpaceHomeV2.
      // We would need to migrate to the new useGlobalSpace context to avoid this issue.
      // https://www.notion.so/highnotefm/Speed-Optimization-Update-SpaceHomeV2-to-use-new-global-state-management-instead-of-useSpaceContext-1ab15dd1482880b19b2dcb5d0bb74e01?pvs=4
      if (!config.entity) return;

      // If user is already a member, or guest on private inbox, don't show dialog
      if (user && config.entity.readableByV2?.includes(user?.id)) return;
      if (isSpacePrivateInboxEnabled(config.entity)) return;
      if (!force && dismissedJoinEntityRef.current) return;
      if (isPublicView(config.entity)) return;
      joinEntityConfigRef.current = config;
      setShowJoinEntity(true);
    },
    [!!user, isPublicView],
  );

  const value = useMemo(() => {
    const authLoading = loading || userLoading;

    // For Cypress (see cypress/support/commands.js)
    setTestProperty("authUser", user);

    return {
      user,
      userMetadata,
      shareKey,
      authLoading,
      refreshAuth,
      refreshShareKey,
      joinEntity,
      storageLimit,
      activeSpacesLimit,
      trackVersionsLimit,
      hideWelcomeSurvey,
      setHideWelcomeSurvey,
      isPublicView,
      isAllowed: (
        key: PERMISSION,
        entities: {
          space?: Space;
          comment?: Comment;
          track?: Track;
          file?: FileEntity;
          spaceTrackRow?: SpaceTrackRow;
        },
      ) =>
        isAllowed(
          key,
          /* The reactive identity only on the flagged branch. A swap on the
           * unflagged branch is a behavior change hidden inside a data
           * migration, and `getAuthId()` is what answered every consumer
           * until now. */
          membersV2Enabled ? reactiveAuthId : authId,
          entities,
          membershipResolver,
        ) || isAllowed(key, shareKey, entities, membershipResolver),
    };
  }, [
    user,
    userMetadata,
    loading,
    userLoading,
    refreshAuth,
    refreshShareKey,
    joinEntity,
    storageLimit,
    activeSpacesLimit,
    trackVersionsLimit,
    hideWelcomeSurvey,
    setHideWelcomeSurvey,
    isPublicView,
    shareKey,
    reactiveAuthId,
    membersV2Enabled,
    membershipResolver,
  ]);

  return (
    <AuthContext.Provider value={value}>
      <Helmet>
        <body data-is-logged-in={!!user} />
      </Helmet>

      {loading || userLoading || isCheckingInvite ? (
        <LoadingScreen />
      ) : showWelcomeSurvey ? (
        <WelcomeSurvey
          userId={user.id}
          welcomeBack={!firstLoginRef.current}
          hasEmail={!!user.email}
          hasFirstName={!!user.firstName}
          hasLastName={!!user.lastName}
          userSubscriptionTier={
            user.subscriptionTier &&
            user.subscriptionTier !== SUBSCRIPTION_TIER.FREE
              ? user.subscriptionTier
              : undefined
          }
        />
      ) : (
        children
      )}

      <JoinEntityDialog
        config={joinEntityConfigRef.current}
        open={showJoinEntity}
        onClose={() => {
          setShowJoinEntity(false);
        }}
        onDismiss={() => (dismissedJoinEntityRef.current = true)}
      />
    </AuthContext.Provider>
  );
};

export const AuthContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { shouldRedirectToMobile } = useMobileAppParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [shareKey, setShareKey] = useState<string>();
  const [userMetadata, setUserMetadata] = useState<UserMetadata>();
  const [currentUserId, setCurrentUserId] = useState<string>();
  const [authId, setAuthId] = useState<string>();
  const [membershipFlags, setMembershipFlags] =
    useState<MembershipClientFlags>();
  const [hideWelcomeSurvey, setHideWelcomeSurvey] = useState<boolean>(
    new URLSearchParams(location.search).has("hideWelcomeSurvey"),
  );

  // Ideally, this should be called only once when auth state changes.
  // However, we have limited access to other parts and states of the app inside
  // the onAuthStateChanged callback, so we instead rely on debouncing this function
  // for the time being.
  const refreshAuth = useMemo(
    () =>
      debounce(async () => {
        try {
          setLoading(true);
          checkIfComesFromMobileApp();
          const firebaseUser = highnote.__firebaseAuth.currentUser;
          const isFromMobileApp =
            sessionStorage.getItem(FROM_MOBILE_APP_FLAG) === "true";
          const publicMe = await highnote.getUserPublic({
            id: firebaseUser.uid,
            url: sanitizeCurrentURL([MOBILE_TOKEN_FLAG]),
            generateFirebaseToken: isFromMobileApp,
          });
          setUserMetadata(publicMe?.userMetadata);
          setMembershipFlags(publicMe?.membershipFlags);

          if (
            shouldRedirectToMobile &&
            publicMe?.firebaseToken &&
            publicMe?.completedWelcomeSurvey
          ) {
            handleMobileAppRedirect(publicMe.firebaseToken);
          }
          firebaseUser.getIdTokenResult(true).then((idTokenResult) => {
            const _shareKey = idTokenResult?.claims?.shareKey;
            setShareKey(_shareKey);
          });
        } catch (e) {
          console.error("Error refreshing auth", e);
        } finally {
          setLoading(false);
        }
      }, 200),
    [],
  );

  useEffect(() => {
    const unsubscribe = highnote.__firebaseAuth.onAuthStateChanged(() => {
      setLoading(true);
      const firebaseUser = highnote.__firebaseAuth.currentUser;

      checkIfComesFromMobileApp();

      if (!firebaseUser) {
        signInAnonymously(highnote.__firebaseAuth);
        return;
      }

      /* The reactive mirror of `getAuthId()`. The legacy engine keeps its
       * direct `getAuthId()` read; only the member-doc listeners consume
       * this state, and they must never evaluate a revocation against a
       * stale identity. The provider body reads `getAuthId()`, and that read
       * is not in the value memo's dependency array, so the memo's closure
       * can capture a stale one.
       *
       * We set this for anonymous users too. An anonymous Firebase uid is a
       * real identity shape (the code detects it by the absence of `|`) and
       * can hold grants, and the branch just below deliberately leaves
       * `currentUserId` undefined for anonymous users. `currentUserId` here
       * silently excludes every anonymous share visitor, exactly the users
       * the share-link flows serve.
       *
       * We set this after the early return above: on the transient callback
       * where `firebaseUser` is null, we leave the previous value alone
       * rather than blank it and restart the listeners for nothing. */
      setAuthId(firebaseUser.uid);

      if (firebaseUser.isAnonymous) {
        setCurrentUserId(undefined);
        setUserMetadata(undefined);
      } else {
        setCurrentUserId(firebaseUser.uid);
      }

      setPersistence(highnote.__firebaseAuth, indexedDBLocalPersistence);
      refreshAuth();
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const checkIfComesFromMobileApp = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const comesFromMobile = urlParams.get(FROM_MOBILE_APP_FLAG) === "true";
    if (comesFromMobile) {
      sessionStorage.setItem(FROM_MOBILE_APP_FLAG, "true");
    }
  };

  const refreshShareKey = async () => {
    const firebaseUser = highnote.__firebaseAuth.currentUser;
    const idTokenResult = await firebaseUser.getIdTokenResult(true);
    const _shareKey = idTokenResult?.claims?.shareKey;
    setShareKey(_shareKey);
  };

  const readyToUseContext = !loading || currentUserId;
  /* Both new providers sit inside `baseComponent`, and both branches of the
   * ternary below render it, so there is exactly one instance either way.
   * They do nothing today: `MyMembershipsContextProvider` opens no listener
   * until at least one membership flag reads true, and no flag reads true
   * today. */
  const baseComponent = (
    /* Warning: this provider stays outermost, and inside `baseComponent`
     * for the same reason the two providers below are: both branches of the
     * ternary render it, so there is exactly one instance either way.
     *
     * An earlier version of this branch created `PublicRolesContextProvider`
     * but never mounted it. Both of its contexts default to values that do
     * nothing (`record` is a `noop`, and `getPublicRoles` returns
     * `EMPTY_ROLES`), so nothing threw and nothing logged; the cache simply
     * recorded nothing and answered `[]` to everything.
     *
     * That is harmless at `spaceMembership: legacy`, because
     * `membershipResolver` is `undefined` there and nothing consults the
     * resolver. It is not harmless the moment the flag moves:
     * `getEffectiveRoles` merges `getPublicRoles(spaceId)` into every
     * identity's roles, so an always-empty merge silently grants too little
     * on every public space, precisely the failure that the resolver's own
     * doc comment forbids. The symptom: controls disappear for visitors
     * while the access answer stays correct, which makes this bug very hard
     * to see. */
    <PublicRolesContextProvider>
      <MembershipFlagsContextProvider flags={membershipFlags}>
        <MyMembershipsContextProvider authId={authId} shareKey={shareKey}>
          <_AuthContextProvider
            loading={loading}
            userMetadata={userMetadata}
            hideWelcomeSurvey={hideWelcomeSurvey}
            shareKey={shareKey}
            reactiveAuthId={authId}
            refreshAuth={refreshAuth}
            refreshShareKey={refreshShareKey}
            setHideWelcomeSurvey={setHideWelcomeSurvey}
          >
            {children}
          </_AuthContextProvider>
        </MyMembershipsContextProvider>
      </MembershipFlagsContextProvider>
    </PublicRolesContextProvider>
  );

  return readyToUseContext ? (
    <UserContextProvider id={currentUserId}>
      {baseComponent}
    </UserContextProvider>
  ) : (
    baseComponent
  );
};

export const useAuth = () => useContext(AuthContext);

export const getAuthId = () => {
  return highnote.__firebaseAuth.currentUser?.uid;
};
